import { ContractTier, MarketType, PerpMarketAccount } from '@drift-labs/sdk';
import { matchEnum } from '@drift/common';
import { useEffect, useState } from 'react';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import { PerpMarketInfo, SpotMarketInfo } from '../stores/types';
import useMarketsInfoStore from '../stores/useMarketsInfoStore';
import { singletonHook } from 'react-singleton-hook';

type MarketInfo = {
	showInsuranceFundWarning: boolean;
	type: MarketType;
	info: PerpMarketInfo | SpotMarketInfo;
};

const DEFAULT_STATE: MarketInfo = {
	showInsuranceFundWarning: false,
	type: MarketType.PERP,
	info: undefined as PerpMarketInfo,
};

/**
 * This hook keeps the full info (config, account state) for all of the perp and spot markets up to date in the store
 */
const useInfoForCurrentlySelectedMarket = () => {
	const allMarketsInfo = useMarketsInfoStore();
	const currentlySelectedMarket = useDriftStore(
		(s) => s.selectedMarket.current
	);

	const [currentMarketInfo, setCurrentMarketInfo] = useState(DEFAULT_STATE);

	useEffect(() => {
		if (!currentlySelectedMarket) {
			setCurrentMarketInfo(DEFAULT_STATE);
			return;
		}

		if (!allMarketsInfo.initialized) {
			setCurrentMarketInfo(DEFAULT_STATE);
			return;
		}

		const marketType = currentlySelectedMarket.marketType;
		const marketIndex = currentlySelectedMarket.marketIndex;

		const info = allMarketsInfo.getMarketInfoByIndexAndType(
			marketIndex,
			marketType
		);

		if (!info) {
			setCurrentMarketInfo(DEFAULT_STATE);
			return;
		}

		setCurrentMarketInfo({
			showInsuranceFundWarning: matchEnum(marketType, MarketType.SPOT)
				? false
				: matchEnum(
						(info.account as PerpMarketAccount).contractTier,
						ContractTier.SPECULATIVE
				  ),
			type: marketType,
			info,
		});
	}, [currentlySelectedMarket, allMarketsInfo?.initialized]);

	return currentMarketInfo;
};

export default singletonHook(DEFAULT_STATE, useInfoForCurrentlySelectedMarket);
